<div class="bgWhiteColor relative flex min-h-screen items-center justify-center overflow-hidden">
  <!-- Ellipse Decoration -->
  <div class="bg-ellipse"></div>

  <div class="relative z-10 my-8 flex min-h-[580px] w-[1024px] overflow-hidden rounded-[20px] shadow-lg">
    <app-image-side
      [imageSrc]="'assets/testlogo.png'"
      [title]="'Join PIMS Today'"
      [description]="'Create your free account and start managing your medicine inventory today.'"
      [type]="'register'" />

    <div class="bg-bg flex w-full flex-col gap-4 px-10 py-8">
      <div>
        <h2 class="text-[32px] font-semibold" i18n="@@registerTitle">Register</h2>
        <h4 class="text-[16px] font-normal" i18n="@@registerSubtitle">Please enter your information</h4>
      </div>

      <!-- Form -->
      <form [formGroup]="registerForm" (ngSubmit)="onSubmit()" class="flex w-full flex-col gap-5">
        <!-- Global Error for Email -->
        @if (apiError()) {
          <app-error [apiError]="apiError()" />
        }

        <!-- Name Fields -->
        <div class="flex gap-4">
          <div class="w-1/2">
            <label for="firstname" class="mb-1 block cursor-pointer" i18n="@@registerFirstNameLabel">First name</label>
            <input
              id="firstname"
              type="text"
              formControlName="firstName"
              class="w-full rounded-[10px] border-2 p-2 py-3 focus:outline-none"
              [class.border-gray-400]="!isFieldInvalid('firstName')"
              [class.border-red-500]="isFieldInvalid('firstName')" />
            @if (hasError("firstName", "required")) {
              <p class="mt-1 text-sm text-red-600" i18n="@@registerFirstNameRequired">First name is required</p>
            } @else if (hasError("firstName", "minlength") && !hasError("firstName", "pattern")) {
              <p class="mt-1 text-sm text-red-600" i18n="@@registerFirstNameMinLength">
                First name must be at least 2 characters
              </p>
            } @else if (hasError("firstName", "maxlength")) {
              <p class="mt-1 text-sm text-red-600" i18n="@@registerFirstNameMaxLength">
                First name must be at most 100 characters
              </p>
            }
            @if (hasError("firstName", "pattern")) {
              <p class="mt-1 text-sm text-red-600" i18n="@@registerFirstNamePattern">
                First Name can only contain letters and spaces
              </p>
            }
          </div>

          <div class="w-1/2">
            <label for="lastname" class="mb-1 block cursor-pointer" i18n="@@registerLastNameLabel">Last name</label>
            <input
              id="lastname"
              type="text"
              formControlName="lastName"
              class="w-full rounded-[10px] border-2 p-2 py-3 focus:outline-none"
              [class.border-gray-400]="!isFieldInvalid('lastName')"
              [class.border-red-500]="isFieldInvalid('lastName')" />
            @if (hasError("lastName", "required")) {
              <p class="mt-1 text-sm text-red-600" i18n="@@registerLastNameRequired">Last name is required</p>
            } @else if (hasError("lastName", "minlength") && !hasError("lastName", "pattern")) {
              <p class="mt-1 text-sm text-red-600" i18n="@@registerLastNameMinLength">
                Last name must be at least 2 characters
              </p>
            } @else if (hasError("lastName", "maxlength")) {
              <p class="mt-1 text-sm text-red-600" i18n="@@registerLastNameMaxLength">
                Last name must be at most 25 characters
              </p>
            }
            @if (hasError("lastName", "pattern")) {
              <p class="mt-1 text-sm text-red-600" i18n="@@registerLastNamePattern">
                Last Name can only contain letters and spaces
              </p>
            }
          </div>
        </div>

        <!-- Email -->
        <div>
          <label for="email" class="mb-1 block cursor-pointer" i18n="@@registerEmailLabel">Email</label>
          <input
            id="email"
            type="email"
            formControlName="email"
            class="emailInput w-full rounded-[10px] border-2 p-2 py-3 focus:outline-none"
            [class.border-gray-400]="!isFieldInvalid('email')"
            [class.border-red-500]="isFieldInvalid('email')"
            dir="ltr"
            placeholder="example@domain.com" />
          @if (hasError("email", "required")) {
            <p class="mt-1 text-sm text-red-600" i18n="@@registerEmailRequired">Email is required</p>
          } @else if (hasError("email", "email") || hasError("email", "pattern")) {
            <p class="mt-1 text-sm text-red-600" i18n="@@registerEmailInvalid">Please enter a valid email address</p>
          } @else if (hasError("email", "minlength")) {
            <p class="mt-1 text-sm text-red-600" i18n="@@registerEmailMinLength">Email must be at least 5 characters</p>
          } @else if (hasError("email", "maxlength")) {
            <p class="mt-1 text-sm text-red-600" i18n="@@registerEmailMaxLength">
              Email must be at most 150 characters
            </p>
          }
        </div>

        <!-- Mobile -->
        <app-mobile-input formControlName="mobileNumber" [error]="isFieldInvalid('mobileNumber')" />
        @if (hasError("mobileNumber", "required")) {
          <p class="mt-[-18px] text-sm text-red-600" i18n="@@registerMobileRequired">Mobile number is required</p>
        } @else if (hasError("mobileNumber", "invalidCharacters")) {
          <p class="mt-[-18px] text-sm text-red-600" i18n="@@registerMobileInvalidPattern">
            Mobile Number must contain only numbers
          </p>
        } @else if (hasError("mobileNumber", "mobileNumber") || hasError("mobileNumber", "invalidLength")) {
          <p class="mt-[-18px] text-sm text-red-600" i18n="@@registerMobileInvalidPatternNumber">
            Invalid Mobile Number
          </p>
        }

        <!-- Password -->
        <div>
          <label for="password" class="mb-1 block cursor-pointer" i18n="@@registerPasswordLabel">Password</label>
          <div
            class="relative rounded-[10px] border-2"
            [class.border-gray-400]="!isFieldInvalid('password')"
            [class.border-red-500]="isFieldInvalid('password')">
            <input
              [type]="showPassword ? 'text' : 'password'"
              id="password"
              formControlName="password"
              class="w-[90%] p-2 py-3 focus:outline-none" />
            @if (!showPassword) {
              <svg
                (click)="showPassword = !showPassword"
                xmlns="http://www.w3.org/2000/svg"
                width="1.25em"
                height="1.25em"
                viewBox="0 0 24 24"
                [class]="isArabic() ? 'left-4' : 'right-4'"
                class="absolute top-1/2 -translate-y-1/2 cursor-pointer">
                <path
                  class="stroke-gray-muted fill-gray-muted"
                  d="M12 9.005a4 4 0 1 1 0 8a4 4 0 0 1 0-8m0 1.5a2.5 2.5 0 1 0 0 5a2.5 2.5 0 0 0 0-5M12 5.5c4.613 0 8.596 3.15 9.701 7.564a.75.75 0 1 1-1.455.365a8.504 8.504 0 0 0-16.493.004a.75.75 0 0 1-1.456-.363A10 10 0 0 1 12 5.5"
                  stroke-width="0.5" />
              </svg>
            } @else {
              <svg
                (click)="showPassword = !showPassword"
                xmlns="http://www.w3.org/2000/svg"
                width="1.25em"
                height="1.25em"
                viewBox="0 0 16 16"
                [class]="isArabic() ? 'left-4' : 'right-4'"
                class="absolute top-1/2 -translate-y-1/2 cursor-pointer">
                <path
                  class="stroke-gray-muted fill-gray-muted"
                  d="m10.12 10.827l4.026 4.027a.5.5 0 0 0 .708-.708l-13-13a.5.5 0 1 0-.708.708l3.23 3.23A6 6 0 0 0 3.2 6.182a6.7 6.7 0 0 0-1.117 1.982c-.021.061-.047.145-.047.145l-.018.062s-.076.497.355.611a.5.5 0 0 0 .611-.355l.001-.003l.008-.025l.035-.109a5.7 5.7 0 0 1 .945-1.674a5 5 0 0 1 1.124-1.014L6.675 7.38a2.5 2.5 0 1 0 3.446 3.446m-.74-.74A1.5 1.5 0 1 1 7.413 8.12zM6.32 4.2l.854.854Q7.564 5 8 5c2.044 0 3.286.912 4.028 1.817a5.7 5.7 0 0 1 .945 1.674q.025.073.035.109l.008.025v.003l.001.001a.5.5 0 0 0 .966-.257v-.003l-.001-.004l-.004-.013a2 2 0 0 0-.06-.187a6.7 6.7 0 0 0-1.117-1.982C11.905 5.089 10.396 4 8.002 4c-.618 0-1.177.072-1.681.199"
                  stroke-width="0.5" />
              </svg>
            }
          </div>
          @if (hasError("password", "required")) {
            <p class="mt-1 text-sm text-red-600" i18n="@@registerPasswordRequired">Password is required</p>
          } @else if (hasError("password", "minlength")) {
            <p class="mt-1 text-sm text-red-600" i18n="@@registerPasswordMinLength">
              Password must be at least 8 characters
            </p>
          } @else if (hasError("password", "maxlength")) {
            <p class="mt-1 text-sm text-red-600" i18n="@@registerPasswordMaxLength">
              Password must be at most 50 characters
            </p>
          } @else if (hasError("password", "passwordMissingLowercase")) {
            <p class="mt-1 text-sm text-red-600" i18n="@@registerPasswordLowercase">
              Password must contain at least one lowercase letter
            </p>
          } @else if (hasError("password", "passwordMissingUppercase")) {
            <p class="mt-1 text-sm text-red-600" i18n="@@passwordMissingUppercase">
              Password must contain at least one uppercase letter
            </p>
          } @else if (hasError("password", "passwordMissingNumber")) {
            <p class="mt-1 text-sm text-red-600" i18n="@@passwordMissingNumber">
              Password must contain at least one number
            </p>
          } @else if (hasError("password", "passwordMissingSpecial")) {
            <p class="mt-1 text-sm text-red-600" i18n="@@registerPasswordSpecial">
              Password must contain at least one special character (! &#64; # $ % ^ &amp; * ( ) &#123; &#125; [ ] - _ =
              + &#47; &#92; ~ . &lt; &gt; : ; &quot; &#39;)
            </p>
          } @else if (hasError("password", "passwordContainsSpaces")) {
            <p class="mt-1 text-sm text-red-600" i18n="@@registerPasswordSpaces">Password cannot contain spaces</p>
          }
        </div>

        <!-- Confirm Password -->
        <div>
          <label for="confirmPassword" class="mb-1 block cursor-pointer" i18n="@@registerConfirmPasswordLabel">
            Confirm Password
          </label>
          <div
            class="relative rounded-[10px] border-2"
            [class.border-gray-400]="!isConfirmPasswordMismatch() && !isFieldInvalid('confirmPassword')"
            [class.border-red-500]="isConfirmPasswordMismatch() || isFieldInvalid('confirmPassword')">
            <input
              [type]="showConfirmPassword ? 'text' : 'password'"
              id="confirmPassword"
              formControlName="confirmPassword"
              class="w-[90%] p-2 py-3 focus:outline-none" />

            @if (!showConfirmPassword) {
              <svg
                (click)="showConfirmPassword = !showConfirmPassword"
                xmlns="http://www.w3.org/2000/svg"
                width="1.25em"
                height="1.25em"
                viewBox="0 0 24 24"
                [class]="isArabic() ? 'left-4' : 'right-4'"
                class="absolute top-1/2 -translate-y-1/2 cursor-pointer">
                <path
                  class="stroke-gray-muted fill-gray-muted"
                  d="M12 9.005a4 4 0 1 1 0 8a4 4 0 0 1 0-8m0 1.5a2.5 2.5 0 1 0 0 5a2.5 2.5 0 0 0 0-5M12 5.5c4.613 0 8.596 3.15 9.701 7.564a.75.75 0 1 1-1.455.365a8.504 8.504 0 0 0-16.493.004a.75.75 0 0 1-1.456-.363A10 10 0 0 1 12 5.5"
                  stroke-width="0.5" />
              </svg>
            } @else {
              <svg
                (click)="showConfirmPassword = !showConfirmPassword"
                xmlns="http://www.w3.org/2000/svg"
                width="1.25em"
                height="1.25em"
                viewBox="0 0 16 16"
                [class]="isArabic() ? 'left-4' : 'right-4'"
                class="absolute top-1/2 -translate-y-1/2 cursor-pointer">
                <path
                  class="stroke-gray-muted fill-gray-muted"
                  d="m10.12 10.827l4.026 4.027a.5.5 0 0 0 .708-.708l-13-13a.5.5 0 1 0-.708.708l3.23 3.23A6 6 0 0 0 3.2 6.182a6.7 6.7 0 0 0-1.117 1.982c-.021.061-.047.145-.047.145l-.018.062s-.076.497.355.611a.5.5 0 0 0 .611-.355l.001-.003l.008-.025l.035-.109a5.7 5.7 0 0 1 .945-1.674a5 5 0 0 1 1.124-1.014L6.675 7.38a2.5 2.5 0 1 0 3.446 3.446m-.74-.74A1.5 1.5 0 1 1 7.413 8.12zM6.32 4.2l.854.854Q7.564 5 8 5c2.044 0 3.286.912 4.028 1.817a5.7 5.7 0 0 1 .945 1.674q.025.073.035.109l.008.025v.003l.001.001a.5.5 0 0 0 .966-.257v-.003l-.001-.004l-.004-.013a2 2 0 0 0-.06-.187a6.7 6.7 0 0 0-1.117-1.982C11.905 5.089 10.396 4 8.002 4c-.618 0-1.177.072-1.681.199"
                  stroke-width="0.5" />
              </svg>
            }
            <!-- <i
              class="fas password-eye-icon"
              [class.fa-eye]="!showConfirmPassword"
              [class.fa-eye-slash]="showConfirmPassword"
              (click)="showConfirmPassword = !showConfirmPassword"></i> -->
          </div>
          @if (hasError("confirmPassword", "required")) {
            <p class="mt-1 text-sm text-red-600" i18n="@@registerConfirmPasswordRequired">
              Confirm password is required
            </p>
          } @else if (isConfirmPasswordMismatch()) {
            <p class="mt-1 text-sm text-red-600" i18n="@@registerPasswordMismatch">Passwords do not match</p>
          }
        </div>
        <!-- Submit -->
        <div class="mt-3 flex justify-end">
          <button type="submit" [disabled]="loading()" class="btn-primary rounded-lg">
            @if (loading()) {
              <span i18n="@@registerButtonLoading">Registering...</span>
            } @else {
              <span i18n="@@registerButtonText">REGISTER</span>
            }
          </button>
        </div>
      </form>

      <div class="flex flex-col items-center justify-center gap-1">
        <p class="text-center text-gray-600">
          <span i18n="@@registerHaveAccount">Already have an account?</span>
        </p>
        <a routerLink="/login" class="text-center text-gray-600 underline" i18n="@@registerLoginLink">Login</a>
      </div>
    </div>
  </div>
</div>

@if (isNotificationVisible()) {
  <app-successful-notification
    [type]="'success'"
    title="Registration Successful"
    i18n-title="@@registerSuccessfulNotificationTitle" />
}
